import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import countryList from "react-select-country-list";
import { getCustomerByName } from "../api/customer";

const countryOptions = countryList().getData();

function getCountryCode(countryLabel) {
  const found = countryOptions.find((option) => option.label === countryLabel);
  return found ? found.value.toLowerCase() : "dk";
}

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-UK", {
    year: "numeric",
    month: "short", // Sep
    day: "numeric", // 5
    hour: "2-digit", // 01
    minute: "2-digit", // 07
    second: "2-digit", // 09
  });
}

export default function CustomerCard({ customer, onSave }) {
  const [editMode, setEditMode] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState(customer);
  const [errors, setErrors] = useState({});

  const handleStartEdit = () => {
    setEditedCustomer({ ...customer });
    setEditMode(true);
    setErrors({});
  };

  const handleCancel = () => {
    setEditedCustomer(customer);
    setEditMode(false);
    setErrors({});
  };

  const checkNameExists = async (name) => {
    if (!name || name === customer.uk_name) return false; // Skip if unchanged

    try {
      const result = await getCustomerByName(name);
      // If exists and it's a different customer (different pk_id)
      return result.exists && result.customer.pk_id !== customer.pk_id;
    } catch (err) {
      console.error("Error checking customer name:", err);
      return false;
    }
  };

  const handleFieldChange = async (field, value) => {
    setEditedCustomer((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (value) => {
    console.log("Country selected:", value); // Debug log
    setEditedCustomer((prev) => ({
      ...prev,
      customer_country: value,
      customer_phoneCountry: getCountryCode(value),
    }));
  };

  const handlePhoneChange = (value, data) => {
    setEditedCustomer((prev) => ({
      ...prev,
      customer_telephone: value,
      customer_phoneCountry: data.countryCode || prev.customer_phoneCountry,
    }));
  };

  const validate = async () => {
    console.log(editedCustomer);
    const newErrors = {};

    if (!editedCustomer.uk_name) {
      newErrors.uk_name = "Name is required";
    } else {
      const exists = await checkNameExists(editedCustomer.uk_name);
      if (exists) {
        newErrors.uk_name = "Customer with this name already exists";
      }
    }

    if (!editedCustomer.customer_address) {
      newErrors.customer_address = "Address is required";
    }
    if (!editedCustomer.customer_city) {
      newErrors.customer_city = "City is required";
    }
    if (!editedCustomer.customer_county) {
      newErrors.customer_county = "County is required";
    }
    if (!editedCustomer.customer_postal_code) {
      newErrors.customer_postal_code = "Postal code is required";
    }
    if (!editedCustomer.customer_country) {
      newErrors.customer_country = "Country is required";
    }
    if (
      editedCustomer.customer_email &&
      !/\S+@\S+\.\S+/.test(editedCustomer.customer_email)
    ) {
      newErrors.customer_email = "Invalid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    const isValid = await validate();
    if (!isValid) return;

    try {
      await onSave(editedCustomer);
      setEditMode(false);
    } catch (err) {
      // Display error from backend
      setErrors((prev) => ({
        ...prev,
        general: err.message || "Failed to save customer",
      }));
    }
  };

  return (
    <>
      <Card>
        <CardContent sx={{ m: 3, width: "400px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">Customer Details</Typography>
            {!editMode ? (
              <EditSquareIcon
                onClick={handleStartEdit}
                sx={{
                  cursor: "pointer",
                  color: "primary.main",
                  "&:hover": { opacity: 0.7 },
                }}
              />
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <SaveIcon
                  onClick={handleSave}
                  sx={{
                    cursor: "pointer",
                    color: "success.main",
                    "&:hover": { opacity: 0.7 },
                  }}
                />
                <CancelIcon
                  onClick={handleCancel}
                  sx={{
                    cursor: "pointer",
                    color: "error.main",
                    "&:hover": { opacity: 0.7 },
                  }}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              size={{ sx: 12, md: 12 }}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Typography sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}>
                Name:
              </Typography>
              <TextField
                variant="standard"
                value={
                  editMode
                    ? editedCustomer?.uk_name || ""
                    : customer.uk_name || ""
                }
                onChange={(e) => handleFieldChange("uk_name", e.target.value)}
                disabled={!editMode}
                fullWidth
                size="small"
                error={!!errors.uk_name}
                helperText={errors.uk_name}
                slotProps={{
                  input: {
                    disableUnderline: !editMode,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "0.875rem",
                  },
                  fontSize: "0.875rem",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}>
                Address:
              </Typography>
              <TextField
                variant="standard"
                value={
                  editMode
                    ? editedCustomer?.customer_address || ""
                    : customer.customer_address || ""
                }
                onChange={(e) =>
                  handleFieldChange("customer_address", e.target.value)
                }
                disabled={!editMode}
                fullWidth
                size="small"
                multiline
                rows={1}
                error={!!errors.customer_address}
                helperText={errors.customer_address}
                slotProps={{
                  input: {
                    disableUnderline: !editMode,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "0.875rem",
                  },
                  fontSize: "0.875rem",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}
              ></Typography>
              <TextField
                variant="standard"
                value={
                  editMode
                    ? editedCustomer?.customer_address_2 || ""
                    : customer.customer_address_2 || ""
                }
                onChange={(e) =>
                  handleFieldChange("customer_address_2", e.target.value)
                }
                disabled={!editMode}
                fullWidth
                size="small"
                placeholder={editMode ? "Apartment, suite, unit, etc." : ""}
                slotProps={{
                  input: {
                    disableUnderline: !editMode,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "0.875rem",
                  },
                  fontSize: "0.875rem",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}
                >
                  City:
                </Typography>
                <TextField
                  variant="standard"
                  value={
                    editMode
                      ? editedCustomer?.customer_city || ""
                      : customer.customer_city || ""
                  }
                  onChange={(e) =>
                    handleFieldChange("customer_city", e.target.value)
                  }
                  disabled={!editMode}
                  fullWidth
                  size="small"
                  error={!!errors.customer_city}
                  helperText={errors.customer_city}
                  slotProps={{
                    input: {
                      disableUnderline: !editMode,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                      color: "rgba(0, 0, 0, 0.87)",
                      fontSize: "0.875rem",
                    },
                    fontSize: "0.875rem",
                  }}
                />
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}
                >
                  County:
                </Typography>
                <TextField
                  variant="standard"
                  value={
                    editMode
                      ? editedCustomer?.customer_county || ""
                      : customer.customer_county || ""
                  }
                  onChange={(e) =>
                    handleFieldChange("customer_county", e.target.value)
                  }
                  disabled={!editMode}
                  fullWidth
                  size="small"
                  error={!!errors.customer_county}
                  helperText={errors.customer_county}
                  slotProps={{
                    input: {
                      disableUnderline: !editMode,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                      color: "rgba(0, 0, 0, 0.87)",
                      fontSize: "0.875rem",
                    },
                    fontSize: "0.875rem",
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}
                >
                  Postal:
                </Typography>
                <TextField
                  variant="standard"
                  value={
                    editMode
                      ? editedCustomer?.customer_postal_code || ""
                      : customer.customer_postal_code || ""
                  }
                  onChange={(e) =>
                    handleFieldChange("customer_postal_code", e.target.value)
                  }
                  disabled={!editMode}
                  fullWidth
                  size="small"
                  error={!!errors.customer_postal_code}
                  helperText={errors.customer_postal_code}
                  slotProps={{
                    input: {
                      disableUnderline: !editMode,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                      color: "rgba(0, 0, 0, 0.87)",
                      fontSize: "0.875rem",
                    },
                    fontSize: "0.875rem",
                  }}
                />
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}
                >
                  Country:
                </Typography>
                <TextField
                  variant="standard"
                  value={
                    editMode
                      ? editedCustomer?.customer_country || ""
                      : customer.customer_country || ""
                  }
                  onChange={(e) => handleCountryChange(e.target.value)}
                  disabled={!editMode}
                  select
                  fullWidth
                  size="small"
                  error={!!errors.customer_country}
                  helperText={errors.customer_country}
                  slotProps={{
                    input: {
                      disableUnderline: !editMode,
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: { style: { maxHeight: 300 } },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                      color: "rgba(0, 0, 0, 0.87)",
                      fontSize: "0.875rem",
                    },
                    fontSize: "0.875rem",
                  }}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {countryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.label}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}>
                Email:
              </Typography>
              <TextField
                variant="standard"
                value={
                  editMode
                    ? editedCustomer?.customer_email || ""
                    : customer.customer_email || ""
                }
                onChange={(e) =>
                  handleFieldChange("customer_email", e.target.value)
                }
                disabled={!editMode}
                fullWidth
                size="small"
                type="email"
                error={!!errors.customer_email}
                helperText={errors.customer_email}
                slotProps={{
                  input: {
                    disableUnderline: !editMode,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "0.875rem",
                  },
                  fontSize: "0.875rem",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}>
                Telephone:
              </Typography>
              <Box sx={{ flex: 1 }}>
                {editMode ? (
                  <>
                    <PhoneInput
                      country={editedCustomer?.customer_phoneCountry ?? "dk"}
                      value={editedCustomer?.customer_telephone ?? ""}
                      onChange={handlePhoneChange}
                      inputStyle={{
                        width: "100%",
                        border: "none",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                        fontSize: "0.875rem",
                      }}
                      containerStyle={{ width: "100%" }}
                      buttonStyle={{
                        border: "none",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                      }}
                    />
                    {errors.customer_telephone && (
                      <Typography color="error" variant="caption">
                        {errors.customer_telephone}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    sx={{ color: "rgba(0, 0, 0, 0.87)", fontSize: "0.875rem" }}
                  >
                    {customer.customer_telephone || ""}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Typography sx={{ minWidth: 60, mt: 1, fontSize: "0.875rem" }}>
                Description:
              </Typography>
              <TextField
                variant="standard"
                value={
                  editMode
                    ? editedCustomer?.customer_description || ""
                    : customer.customer_description || ""
                }
                onChange={(e) =>
                  handleFieldChange("customer_description", e.target.value)
                }
                disabled={!editMode}
                fullWidth
                size="small"
                multiline
                rows={editMode ? 3 : 1}
                slotProps={{
                  input: {
                    disableUnderline: !editMode,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                    marginTop: "6px",
                    paddingLeft: "6px",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "0.875rem",
                  },
                  fontSize: "0.875rem",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 60, mt: -0.5, fontSize: "0.875rem" }}>
                Active:
              </Typography>
              <TextField
                variant="standard"
                value={
                  editMode
                    ? editedCustomer?.active
                      ? "Yes"
                      : "No"
                    : customer.active
                      ? "Yes"
                      : "No"
                }
                onChange={(e) =>
                  handleFieldChange("active", e.target.value === "Yes")
                }
                disabled={!editMode}
                select
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    disableUnderline: !editMode,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "0.875rem",
                  },
                  fontSize: "0.875rem",
                }}
              >
                <MenuItem value="Yes" sx={{ fontSize: "0.875rem" }}>
                  Yes
                </MenuItem>
                <MenuItem value="No" sx={{ fontSize: "0.875rem" }}>
                  No
                </MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 60, fontSize: "0.6rem" }}>
                Created:
              </Typography>
              <Typography sx={{ minWidth: 60, fontSize: "0.6rem" }}>
                {formatDate(customer.created_at) || ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 60, fontSize: "0.6rem" }}>
                Updated:
              </Typography>
              <Typography sx={{ minWidth: 60, fontSize: "0.6rem" }}>
                {formatDate(customer.updated_at) || ""}
              </Typography>
            </Box>

            {errors.general && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errors.general}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
